import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { services } from "../data/services";
import { insights } from "../data/insights";
import CTABlock from "../components/sections/CTABlock";

const featuredExpertise = [
  "civil-litigation",
  "criminal-litigation",
  "commercial-disputes",
  "real-estate-rera",
  "corporate-advisory",
  "contract-drafting",
];

const courts = [
  { title: "District Courts", desc: "Trial-stage representation across civil and criminal forums." },
  { title: "High Courts", desc: "Writs, appeals and constitutional matters before relevant benches." },
  { title: "RERA Authorities", desc: "Homebuyer and developer matters before the regulatory authority." },
  { title: "Tribunals", desc: "Specialised forums across commercial, real estate and regulatory disputes." },
  { title: "Adjudicatory Forums", desc: "Quasi-judicial bodies relevant to the client’s sector and matter." },
  { title: "Supreme Court of India", desc: "Matters capable of escalation, appeal or special leave." },
];

const audience = [
  { title: "Individuals", desc: "Personal disputes, property matters, notices and consultations." },
  { title: "Businesses", desc: "Commercial agreements, recovery, vendor and partner disputes." },
  { title: "Start-ups", desc: "Founder, contract, compliance and risk advisory from day one." },
  { title: "Real Estate Stakeholders", desc: "Buyers, developers, investors and landowners." },
  { title: "Corporate Clients", desc: "Strategic counsel across documentation, disputes and regulation." },
];

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative bg-navy text-ivory overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1736908038690-f22df7e6e152?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3VydGhvdXNlJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3IlMjBmYWNhZGV8ZW58MHx8fHwxNzc4MzEwODg4fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/85 to-navy" aria-hidden="true" />
        <div className="relative container-px max-w-[1400px] mx-auto pt-36 md:pt-44 pb-24 md:pb-32">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-9">
              <div className="overline text-gold mb-7">Lex Polaris &middot; Advocates &amp; Solicitors</div>
              <h1 className="h-display text-5xl md:text-7xl lg:text-[88px] text-ivory">
                Sharp, Strategic Counsel<br />
                <span className="italic font-light text-ivory/85">for complex legal matters.</span>
              </h1>
            </div>
            <div className="md:col-span-12 grid md:grid-cols-12 gap-10 mt-10 md:mt-16">
              <div className="md:col-span-6 md:col-start-7">
                <p className="text-ivory/80 text-base md:text-lg leading-relaxed max-w-xl">
                  Lex Polaris is a research-driven and strategy-focused law practice based in Delhi NCR,
                  providing litigation, dispute resolution and corporate advisory services for individuals,
                  businesses, start-ups, real estate stakeholders and corporate clients.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="hero-cta-book"
                    className="inline-flex items-center gap-2 bg-gold text-deep-ink px-7 py-4 text-sm tracking-[0.16em] uppercase font-medium hover:bg-ivory hover:text-navy transition-colors duration-300"
                  >
                    Book a Consultation
                    <ArrowUpRight size={16} />
                  </Link>
                  <Link
                    to="/expertise"
                    data-testid="hero-cta-expertise"
                    className="btn-outline-light"
                  >
                    Explore Expertise
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST CARDS */}
      <section className="bg-ivory border-b border-soft-grey" data-testid="home-trust-bar">
        <div className="container-px max-w-[1400px] mx-auto py-14 md:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-soft-grey border border-soft-grey">
            {[
              {
                eyebrow: "Experience",
                title: "12+ Years",
                desc: "Active legal practice across civil, criminal and commercial matters.",
              },
              {
                eyebrow: "Litigation",
                title: "Dispute Strategy",
                desc: "Litigation, negotiation and representation across courts and forums.",
              },
              {
                eyebrow: "Advisory",
                title: "Corporate Advisory",
                desc: "Practical legal support for businesses, start-ups and corporate clients.",
              },
              {
                eyebrow: "Property",
                title: "Real Estate & RERA",
                desc: "Advisory and representation for property and regulatory disputes.",
              },
              {
                eyebrow: "Forums",
                title: "Courts & Tribunals",
                desc: "Experience before District Courts, High Courts, RERA Authorities and tribunals.",
              },
            ].map((card, i) => (
              <div
                key={card.title}
                data-testid={`home-trust-card-${i}`}
                className="bg-white p-7 md:p-8 hover:bg-ivory transition-colors duration-300 group"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-gold font-medium">
                    {card.eyebrow}
                  </span>
                  <span className="font-serif text-gold/70 text-xs">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-[22px] md:text-2xl text-navy leading-tight mb-3 group-hover:text-deep-ink transition-colors">
                  {card.title}
                </h3>
                <p className="text-[13px] text-charcoal/65 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-ivory section-y" data-testid="home-about">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 lg:gap-20">
          <div className="md:col-span-5">
            <div className="overline mb-6">The Firm</div>
            <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy">
              A research-driven law practice built around strategy.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="editorial-rule mb-8" />
            <p className="body-lg text-charcoal/80 mb-6">
              Lex Polaris combines detailed legal research, practical strategy and focused representation to
              help clients navigate disputes, documentation, regulatory issues and business-critical legal
              decisions.
            </p>
            <p className="body-lg text-charcoal/75 mb-10">
              The firm advises individuals, businesses, start-ups, real estate stakeholders and corporate
              clients on legally sound, commercially practical and outcome-oriented solutions.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="text-link">
              Know the Firm <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* EXPERTISE PREVIEW */}
      <section className="bg-white border-y border-soft-grey section-y" data-testid="home-expertise">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-14 md:mb-20">
            <div className="md:col-span-6">
              <div className="overline mb-6">Expertise</div>
              <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy">
                Litigation, advisory &amp; legal strategy.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="body-lg text-charcoal/75">
                From courtroom disputes to corporate documentation, Lex Polaris provides clear, strategic and
                practical legal support across key practice areas.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-soft-grey border border-soft-grey">
            {featuredExpertise.map((slug, idx) => {
              const s = services.find((x) => x.slug === slug);
              return (
                <Link
                  key={slug}
                  to={`/expertise/${slug}`}
                  data-testid={`home-expertise-card-${slug}`}
                  className="group bg-white p-9 md:p-11 hover:bg-ivory transition-colors duration-300 block"
                >
                  <div className="flex items-start justify-between mb-7">
                    <span className="font-serif text-gold text-base">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <ArrowUpRight size={18} className="text-navy/50 group-hover:text-gold transition-colors" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-[26px] text-navy leading-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm text-charcoal/70 leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>

          <div className="mt-12 flex justify-start">
            <Link to="/expertise" data-testid="home-expertise-all-cta" className="text-link">
              View All Expertise <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRINCIPAL ADVOCATE PREVIEW */}
      <section className="bg-ivory section-y" data-testid="home-principal">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <div className="aspect-[4/5] overflow-hidden bg-navy/10">
              <img
                src="/brand/varun-singh-hooda.jpeg"
                alt="Varun Singh Hooda, Principal Advocate at Lex Polaris"
                className="w-full h-full object-cover object-top hover:scale-[1.03] transition-transform duration-700"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7 order-1 md:order-2">
            <div className="overline mb-6">Principal Advocate</div>
            <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy mb-7">
              Led by Varun Singh Hooda
            </h2>
            <p className="body-lg text-charcoal/80 mb-8">
              Principal Advocate at Lex Polaris, with 12+ years of active experience in civil, criminal and
              commercial litigation, real estate disputes, RERA proceedings, corporate advisory, legal drafting,
              contract structuring, dispute strategy and client representation before courts and tribunals.
            </p>
            <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6 mb-10">
              {[
                "Principal Advocate",
                "12+ years of active legal practice",
                "Member, Bar Council of Delhi",
                "Member, Delhi High Court Bar Association",
                "Member, Gurugram Bar Association",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-charcoal/85">
                  <span className="mt-2 inline-block w-1.5 h-1.5 bg-gold flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <Link
              to="/principal-advocate"
              data-testid="home-principal-cta"
              className="text-link"
            >
              View Professional Profile <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* COURTS & FORUMS */}
      <section className="bg-navy text-ivory section-y" data-testid="home-courts">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-7">
              <div className="overline text-gold mb-6">Representation</div>
              <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-ivory">
                Across courts, authorities &amp; forums.
              </h2>
            </div>
            <div className="md:col-span-5 self-end">
              <p className="text-ivory/75 text-base md:text-lg leading-relaxed">
                Experience before District Courts, High Courts, RERA Authorities, tribunals and other
                adjudicatory forums.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            {courts.map((c, idx) => (
              <div
                key={c.title}
                data-testid={`home-court-${idx}`}
                className="bg-navy p-8 md:p-10 hover:bg-deep-ink transition-colors"
              >
                <div className="font-serif text-gold text-sm mb-5">{String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-xl md:text-2xl text-ivory mb-3">{c.title}</h3>
                <p className="text-sm text-ivory/70 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE ADVISE */}
      <section className="bg-ivory section-y" data-testid="home-audience">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="max-w-3xl mb-14 md:mb-20">
            <div className="overline mb-6">Who We Advise</div>
            <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy">
              Strategic legal support for diverse clients.
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-px bg-soft-grey border border-soft-grey">
            {audience.map((a, idx) => (
              <div
                key={a.title}
                data-testid={`home-audience-${idx}`}
                className="bg-white p-8 hover:bg-ivory transition-colors"
              >
                <div className="font-serif text-gold text-sm mb-5">{String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-xl text-navy mb-3">{a.title}</h3>
                <p className="text-sm text-charcoal/65 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="bg-white border-t border-soft-grey section-y" data-testid="home-insights">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <div className="overline mb-6">Insights</div>
              <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy">
                Legal insights, updates &amp; perspectives.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="body-lg text-charcoal/70">
                A space for practical legal updates, explainers and perspectives across litigation, corporate
                advisory, real estate, RERA, regulatory developments and dispute strategy.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {insights.slice(0, 3).map((post) => (
              <Link
                key={post.slug}
                to={`/insights#${post.slug}`}
                data-testid={`home-insight-${post.slug}`}
                className="group block"
              >
                <div className="aspect-[4/3] overflow-hidden bg-soft-grey mb-6">
                  <img
                    src={post.image}
                    alt={post.imageAlt || post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="overline text-gold mb-3">{post.category}</div>
                <h3 className="font-serif text-xl md:text-2xl text-navy leading-snug mb-3 group-hover:text-gold transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{post.summary}</p>
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/insights" data-testid="home-insights-all-cta" className="text-link">
              Explore Insights <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABlock
        eyebrow="Consultation"
        heading="Need legal clarity before your next move?"
        body="Speak with Lex Polaris for focused legal guidance across litigation, documentation, disputes and advisory matters."
        primaryLabel="Book a Consultation"
        primaryTo="/contact"
        secondaryLabel="WhatsApp Us"
        secondaryHref="https://wa.me/919810899124?text=Hello%20Lex%20Polaris%20Team%2C%20I%20would%20like%20to%20discuss%20a%20legal%20matter."
      />
    </>
  );
}
